import { onSnapshot, query, orderBy, where, Timestamp, collection, documentId, getDocs, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/FirebaseProvider';

export interface NewsItem {
  id?: string;
  topicId: string;
  topicValue: string;
  topicType?: 'USER' | 'COMMON';
  topic?: string;
  title: string;
  source?: string; // Keep for backward compatibility
  sourceId?: string; // New field with source document ID
  link: string;
  publishedAt: Date;
  createdAt: Date;
  status: 'READY_TO_PROCESS' | 'PROCESSING' | 'PROCESSED' | 'HIDDEN';
  processedAt?: Date;
  // Processing retry tracking
  processingAttempts?: number; // Number of times processing has been attempted
  processingLastAttemptedAt?: Date; // Timestamp of last processing attempt
  // ChatGPT analysis fields
  summary?: string;
  expandedSummary?: string; // 10-sentence detailed summary
  isPaywalled?: boolean;
  tags?: string[];
  actualUrl?: string;
  imageUrls?: string[];
  featuredImageUrl?: string; // Single best image picked by ChatGPT
  language?: string;
}

export interface NewsItemData {
  topicId: string;
  topicValue: string;
  topicType?: 'USER' | 'COMMON';
  topic?: string;
  title: string;
  source?: string; // Keep for backward compatibility
  sourceId?: string; // New field with source document ID
  link: string;
  publishedAt: Timestamp;
  createdAt: Timestamp;
  status: 'READY_TO_PROCESS' | 'PROCESSING' | 'PROCESSED' | 'HIDDEN';
  processedAt?: Timestamp;
  // Processing retry tracking
  processingAttempts?: number; // Number of times processing has been attempted
  processingLastAttemptedAt?: Timestamp; // Timestamp of last processing attempt
  // ChatGPT analysis fields
  summary?: string;
  expandedSummary?: string; // 10-sentence detailed summary
  isPaywalled?: boolean;
  tags?: string[];
  actualUrl?: string;
  imageUrls?: string[];
  featuredImageUrl?: string; // Single best image picked by ChatGPT
  language?: string;
}

// Subscribe to news items in real-time (both ready to process and processed items, but not hidden)
// If user is null (not logged in), only show COMMON topicType items
// If user is logged in, show only news items from their personal collection
// If topicName is provided, filter by that specific topic (kebab-case)
export const subscribeToNewsItems = (
  callback: (newsItems: NewsItem[], hasMore?: boolean, totalCount?: number) => void, 
  user?: any,
  pageSize: number = 5,
  topicName?: string
) => {
  if (user && user.id) {
    // Logged in users see news items from their personal collection
    return subscribeToUserNewsItems(callback, user.id, pageSize, topicName);
  } else {
    // Logged out users only see COMMON topicType items
    // Apply limit based on pageSize and filter to only COMMON items
    let q = query(
      collection(db, 'news-items'), 
      where('status', 'in', ['READY_TO_PROCESS', 'PROCESSING', 'PROCESSED']),
      where('topicType', '==', 'COMMON')
    );

    // Add topic filter if specified
    if (topicName) {
      q = query(q, where('topic', '==', topicName));
    }

    q = query(q, orderBy('publishedAt', 'desc'), limit(pageSize + 1));
    
    return onSnapshot(q, async (querySnapshot) => {
      const newsItems: NewsItem[] = [];
      const docs = querySnapshot.docs.slice(0, pageSize); // Only take the requested amount
      const hasMore = querySnapshot.docs.length > pageSize; // Check if we got more than requested
      
      docs.forEach((doc) => {
        const data = doc.data() as NewsItemData;
        newsItems.push({
          id: doc.id,
          topicId: data.topicId,
          topicValue: data.topicValue,
          topicType: data.topicType,
          topic: data.topic,
          title: data.title,
          source: data.source, // Keep for backward compatibility
          sourceId: data.sourceId, // New field
          link: data.link,
          publishedAt: data.publishedAt.toDate(),
          createdAt: data.createdAt.toDate(),
          status: data.status,
          processedAt: data.processedAt?.toDate(),
          // Map processing retry tracking
          processingAttempts: data.processingAttempts || 0,
          processingLastAttemptedAt: data.processingLastAttemptedAt?.toDate(),
          // Map ChatGPT analysis fields
          summary: data.summary,
          expandedSummary: data.expandedSummary,
          isPaywalled: false, // Temporarily ignore paywall detection
          tags: data.tags,
          actualUrl: data.actualUrl,
          imageUrls: data.imageUrls,
          featuredImageUrl: data.featuredImageUrl,
          language: data.language,
        });
      });
      
      // Get total count for pagination info (for logged-out users)
      let totalCountQuery = query(
        collection(db, 'news-items'), 
        where('status', 'in', ['READY_TO_PROCESS', 'PROCESSING', 'PROCESSED']),
        where('topicType', '==', 'COMMON')
      );

      // Add topic filter for count as well if specified
      if (topicName) {
        totalCountQuery = query(totalCountQuery, where('topic', '==', topicName));
      }

      const totalSnapshot = await getDocs(totalCountQuery);
      const totalCount = totalSnapshot.size;
      
      callback(newsItems, hasMore, totalCount);
    });
  }
};

// Subscribe to news items for a specific user (based on their news-items subcollection)
// With pagination support and real-time updates
// If topicName is provided, filter by that specific topic (kebab-case)
export const subscribeToUserNewsItems = (
  callback: (newsItems: NewsItem[], hasMore: boolean, totalCount: number) => void, 
  userId: string, 
  pageSize: number = 5,
  topicName?: string
) => {
  let newsItemsUnsubscribe: (() => void) | null = null;
  
  if (topicName) {
    // When filtering by topic, we need a different approach:
    // 1. Query the main news-items collection for the specific topic with pagination
    // 2. Check which of those items are in the user's subcollection
    // 3. Return only the items that match both criteria
    
    let mainNewsQuery = query(
      collection(db, 'news-items'),
      where('status', 'in', ['READY_TO_PROCESS', 'PROCESSING', 'PROCESSED']),
      where('topic', '==', topicName),
      orderBy('publishedAt', 'desc'),
      limit(pageSize * 3) // Get more items to account for filtering by user's collection
    );
    
    newsItemsUnsubscribe = onSnapshot(mainNewsQuery, async (querySnapshot) => {
      try {
        // Get all the user's news item IDs first
        const userNewsItemsRef = collection(db, 'users', userId, 'news-items');
        const userNewsItemsSnapshot = await getDocs(userNewsItemsRef);
        const userNewsItemIds = new Set(userNewsItemsSnapshot.docs.map(doc => doc.data().newsItemId));
        
        // Filter to only include items that are in the user's collection
        const filteredDocs = querySnapshot.docs.filter(doc => userNewsItemIds.has(doc.id));
        
        // Apply pagination to the filtered results
        const paginatedDocs = filteredDocs.slice(0, pageSize);
        const hasMore = filteredDocs.length > pageSize;
        
        const newsItems = paginatedDocs.map(doc => {
          const data = doc.data() as NewsItemData;
          return {
            id: doc.id,
            topicId: data.topicId,
            topicValue: data.topicValue,
            topicType: data.topicType,
            topic: data.topic,
            title: data.title,
            source: data.source,
            sourceId: data.sourceId,
            link: data.link,
            publishedAt: data.publishedAt.toDate(),
            createdAt: data.createdAt.toDate(),
            status: data.status,
            processedAt: data.processedAt?.toDate(),
            processingAttempts: data.processingAttempts || 0,
            processingLastAttemptedAt: data.processingLastAttemptedAt?.toDate(),
            summary: data.summary,
            expandedSummary: data.expandedSummary,
            isPaywalled: false,
            tags: data.tags,
            actualUrl: data.actualUrl,
            imageUrls: data.imageUrls,
            featuredImageUrl: data.featuredImageUrl,
            language: data.language,
          } as NewsItem;
        });
        
        // For total count, we count all filtered items (this is the total available for this topic)
        callback(newsItems, hasMore, filteredDocs.length);
        
      } catch (error) {
        console.error('Error in topic-filtered user news items:', error);
        callback([], false, 0);
      }
    });
    
    return () => {
      if (newsItemsUnsubscribe) {
        newsItemsUnsubscribe();
      }
    };
  }
  
  // Original logic for when no topic filter is applied
  let userNewsUnsubscribe: (() => void) | null = null;
  let newsItemsUnsubscribes: (() => void)[] = [];
  let currentNewsItemIds: string[] = [];
  
  // First, subscribe to the user's news-items subcollection to get their news item IDs
  const userNewsItemsRef = collection(db, 'users', userId, 'news-items');
  
  // Get the requested page size from user's subcollection
  const userNewsQuery = query(
    userNewsItemsRef, 
    orderBy('publishedAt', 'desc'),
    limit(pageSize) // Get exactly the requested page size
  );
  
  const setupNewsItemsListeners = async (newsItemIds: string[]) => {
    // Clean up existing news items listeners
    newsItemsUnsubscribes.forEach(unsubscribe => unsubscribe());
    newsItemsUnsubscribes = [];
    
    if (newsItemIds.length === 0) {
      return;
    }
    
    // Firestore 'in' queries are limited to 10 items, so we need to batch them
    const batchSize = 10;
    const newsItemBatches: string[][] = [];
    
    for (let i = 0; i < newsItemIds.length; i += batchSize) {
      newsItemBatches.push(newsItemIds.slice(i, i + batchSize));
    }
    
    // Keep track of all news items across all batches
    const allNewsItemsMap = new Map<string, NewsItem>();
    
    // Set up real-time listeners for each batch
    const batchPromises = newsItemBatches.map((batch, batchIndex) => {
      return new Promise<void>((resolve, reject) => {
        let newsItemsQuery = query(
          collection(db, 'news-items'),
          where('status', 'in', ['READY_TO_PROCESS', 'PROCESSING', 'PROCESSED']),
          where(documentId(), 'in', batch)
        );
        
        const unsubscribe = onSnapshot(
          newsItemsQuery, 
          (snapshot) => {
            // Remove old items from this batch from the map
            batch.forEach(id => allNewsItemsMap.delete(id));
            
            // Add updated items from this batch
            snapshot.forEach((doc) => {
              const data = doc.data() as NewsItemData;
              const newsItem: NewsItem = {
                id: doc.id,
                topicId: data.topicId,
                topicValue: data.topicValue,
                topicType: data.topicType,
                topic: data.topic,
                title: data.title,
                source: data.source,
                sourceId: data.sourceId,
                link: data.link,
                publishedAt: data.publishedAt.toDate(),
                createdAt: data.createdAt.toDate(),
                status: data.status,
                processedAt: data.processedAt?.toDate(),
                // Map processing retry tracking
                processingAttempts: data.processingAttempts || 0,
                processingLastAttemptedAt: data.processingLastAttemptedAt?.toDate(),
                summary: data.summary,
                expandedSummary: data.expandedSummary,
                isPaywalled: false, // Temporarily ignore paywall detection
                tags: data.tags,
                actualUrl: data.actualUrl,
                imageUrls: data.imageUrls,
                featuredImageUrl: data.featuredImageUrl,
                language: data.language,
              };
              
              allNewsItemsMap.set(doc.id, newsItem);
            });
            
            // Convert map to array, preserve order based on original newsItemIds
            const orderedNewsItems: NewsItem[] = [];
            newsItemIds.forEach(id => {
              const item = allNewsItemsMap.get(id);
              if (item) {
                orderedNewsItems.push(item);
              }
            });
            
            // Get pagination info
            let totalCountQuery = query(userNewsItemsRef, orderBy('publishedAt', 'desc'));
            getDocs(totalCountQuery).then(totalSnapshot => {
              const totalCount = totalSnapshot.size;
              const hasMore = totalCount > pageSize;
              
              callback(orderedNewsItems, hasMore, totalCount);
            }).catch(error => {
              console.error('Error getting total count:', error);
              callback(orderedNewsItems, false, 0);
            });
            
            // Resolve the promise after the first snapshot
            if (batchIndex === 0) {
              resolve();
            }
          },
          (error) => {
            console.error(`Error in news items batch ${batchIndex} listener:`, error);
            reject(error);
          }
        );
        
        newsItemsUnsubscribes.push(unsubscribe);
      });
    });
    
    try {
      await Promise.all(batchPromises);
    } catch (error) {
      console.error('Error setting up news items listeners:', error);
    }
  };
  
  // Subscribe to user's news-items subcollection
  userNewsUnsubscribe = onSnapshot(userNewsQuery, async (userNewsSnapshot) => {
    if (userNewsSnapshot.empty) {
      // Clean up existing listeners
      newsItemsUnsubscribes.forEach(unsubscribe => unsubscribe());
      newsItemsUnsubscribes = [];
      currentNewsItemIds = [];
      callback([], false, 0);
      return;
    }
    
    // Get the news item IDs from user's subcollection
    const newsItemIds: string[] = [];
    userNewsSnapshot.docs.forEach((doc) => {
      newsItemIds.push(doc.data().newsItemId);
    });
    
    // Only setup new listeners if the IDs have changed
    if (JSON.stringify(newsItemIds) !== JSON.stringify(currentNewsItemIds)) {
      currentNewsItemIds = newsItemIds;
      await setupNewsItemsListeners(newsItemIds);
    } else {
      // News item IDs unchanged, keeping existing listeners
    }
  });
  
  // Return cleanup function
  return () => {
    if (userNewsUnsubscribe) {
      userNewsUnsubscribe();
    }
    newsItemsUnsubscribes.forEach(unsubscribe => unsubscribe());
    newsItemsUnsubscribes = [];
    currentNewsItemIds = [];
  };
};

// Subscribe to saved news items for a specific user (based on their news-items-saved subcollection)
// With pagination support and real-time updates
export const subscribeToSavedNewsItems = (
  callback: (newsItems: NewsItem[], hasMore: boolean, totalCount: number) => void, 
  userId: string, 
  pageSize: number = 5
) => {
  let userNewsUnsubscribe: (() => void) | null = null;
  let newsItemsUnsubscribes: (() => void)[] = [];
  let currentNewsItemIds: string[] = [];
  
  // First, subscribe to the user's news-items-saved subcollection to get their saved news item IDs
  const userSavedNewsItemsRef = collection(db, 'users', userId, 'news-items-saved');
  
  // Get the requested page size from user's saved subcollection
  const userSavedNewsQuery = query(
    userSavedNewsItemsRef, 
    orderBy('savedAt', 'desc'), // Order by savedAt instead of publishedAt
    limit(pageSize) // Get exactly the requested page size
  );
  
  const setupNewsItemsListeners = async (newsItemIds: string[]) => {
    // Clean up existing news items listeners
    newsItemsUnsubscribes.forEach(unsubscribe => unsubscribe());
    newsItemsUnsubscribes = [];
    
    if (newsItemIds.length === 0) {
      return;
    }
    
    // Firestore 'in' queries are limited to 10 items, so we need to batch them
    const batchSize = 10;
    const newsItemBatches: string[][] = [];
    
    for (let i = 0; i < newsItemIds.length; i += batchSize) {
      newsItemBatches.push(newsItemIds.slice(i, i + batchSize));
    }
    
    // Keep track of all news items across all batches
    const allNewsItemsMap = new Map<string, NewsItem>();
    
    // Set up real-time listeners for each batch
    const batchPromises = newsItemBatches.map((batch, batchIndex) => {
      return new Promise<void>((resolve, reject) => {
        let newsItemsQuery = query(
          collection(db, 'news-items'),
          where('status', 'in', ['READY_TO_PROCESS', 'PROCESSING', 'PROCESSED']),
          where(documentId(), 'in', batch)
        );
        
        const unsubscribe = onSnapshot(
          newsItemsQuery, 
          (snapshot) => {
            // Remove old items from this batch from the map
            batch.forEach(id => allNewsItemsMap.delete(id));
            
            // Add updated items from this batch
            snapshot.forEach((doc) => {
              const data = doc.data() as NewsItemData;
              const newsItem: NewsItem = {
                id: doc.id,
                topicId: data.topicId,
                topicValue: data.topicValue,
                topicType: data.topicType,
                topic: data.topic,
                title: data.title,
                source: data.source,
                sourceId: data.sourceId,
                link: data.link,
                publishedAt: data.publishedAt.toDate(),
                createdAt: data.createdAt.toDate(),
                status: data.status,
                processedAt: data.processedAt?.toDate(),
                // Map processing retry tracking
                processingAttempts: data.processingAttempts || 0,
                processingLastAttemptedAt: data.processingLastAttemptedAt?.toDate(),
                summary: data.summary,
                expandedSummary: data.expandedSummary,
                isPaywalled: false, // Temporarily ignore paywall detection
                tags: data.tags,
                actualUrl: data.actualUrl,
                imageUrls: data.imageUrls,
                featuredImageUrl: data.featuredImageUrl,
                language: data.language,
              };
              
              allNewsItemsMap.set(doc.id, newsItem);
            });
            
            // Convert map to array, preserve order based on original newsItemIds
            const orderedNewsItems: NewsItem[] = [];
            newsItemIds.forEach(id => {
              const item = allNewsItemsMap.get(id);
              if (item) {
                orderedNewsItems.push(item);
              }
            });
            
            // Get pagination info
            let totalCountQuery = query(userSavedNewsItemsRef, orderBy('savedAt', 'desc'));
            getDocs(totalCountQuery).then(totalSnapshot => {
              const totalCount = totalSnapshot.size;
              const hasMore = totalCount > pageSize;
              
              callback(orderedNewsItems, hasMore, totalCount);
            }).catch(error => {
              console.error('Error getting total count:', error);
              callback(orderedNewsItems, false, 0);
            });
            
            // Resolve the promise after the first snapshot
            if (batchIndex === 0) {
              resolve();
            }
          },
          (error) => {
            console.error(`Error in news items batch ${batchIndex} listener:`, error);
            reject(error);
          }
        );
        
        newsItemsUnsubscribes.push(unsubscribe);
      });
    });
    
    try {
      await Promise.all(batchPromises);
    } catch (error) {
      console.error('Error setting up news items listeners:', error);
    }
  };
  
  // Subscribe to user's news-items-saved subcollection
  userNewsUnsubscribe = onSnapshot(userSavedNewsQuery, async (userSavedNewsSnapshot) => {
    if (userSavedNewsSnapshot.empty) {
      // Clean up existing listeners
      newsItemsUnsubscribes.forEach(unsubscribe => unsubscribe());
      newsItemsUnsubscribes = [];
      currentNewsItemIds = [];
      callback([], false, 0);
      return;
    }
    
    // Get the news item IDs from user's saved subcollection
    const newsItemIds: string[] = [];
    userSavedNewsSnapshot.docs.forEach((doc) => {
      newsItemIds.push(doc.data().newsItemId);
    });
    
    // Only setup new listeners if the IDs have changed
    if (JSON.stringify(newsItemIds) !== JSON.stringify(currentNewsItemIds)) {
      currentNewsItemIds = newsItemIds;
      await setupNewsItemsListeners(newsItemIds);
    } else {
      // News item IDs unchanged, keeping existing listeners
    }
  });
  
  // Return cleanup function
  return () => {
    if (userNewsUnsubscribe) {
      userNewsUnsubscribe();
    }
    newsItemsUnsubscribes.forEach(unsubscribe => unsubscribe());
    newsItemsUnsubscribes = [];
    currentNewsItemIds = [];
  };
};

// Remove a news item from user's collection (mark as read)
export const markNewsItemAsRead = async (newsItemId: string): Promise<void> => {
  try {
    const markAsReadFunction = httpsCallable(functions, 'markNewsItemAsRead');
    await markAsReadFunction({ newsItemId });
  } catch (error) {
    console.error('Error calling Firebase function:', error);
    throw error;
  }
};

// Save a news item for later reading
export const saveNewsItemForLater = async (newsItemId: string): Promise<void> => {
  try {
    const saveForLaterFunction = httpsCallable(functions, 'saveNewsItemForLater');
    await saveForLaterFunction({ newsItemId });
  } catch (error) {
    console.error('Error calling Firebase function:', error);
    throw error;
  }
};

// Hide a news item by setting its status to HIDDEN
export const hideNewsItem = async (newsItemId: string): Promise<void> => {
  try {
    const hideNewsItemFunction = httpsCallable(functions, 'hideNewsItem');
    await hideNewsItemFunction({ newsItemId });
  } catch (error) {
    console.error('Error calling Firebase function:', error);
    throw error;
  }
};

// Delete a news item using Firebase Function (admin only)
export const deleteNewsItem = async (newsItemId: string): Promise<void> => {
  try {
    const deleteNewsItemFunction = httpsCallable(functions, 'deleteNewsItem');
    await deleteNewsItemFunction({ newsItemId });
  } catch (error) {
    console.error('Error calling Firebase function:', error);
    throw error;
  }
};

// Request processing for a news item using Firebase Function
export const requestNewsItemProcessing = async (newsItemId: string): Promise<void> => {
  try {
    const requestProcessingFunction = httpsCallable(functions, 'requestNewsItemProcessing');
    await requestProcessingFunction({ newsItemId });
  } catch (error) {
    console.error('Error calling Firebase function:', error);
    throw error;
  }
};